import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import IconButtonWithContextMenu, {
  type IconButtonWithContextMenuProps,
} from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import GiftIcon16 from '@wix/da-ds/pkg/Icons/16x16/Gift';
import { isServer } from '@wix/da-hooks/pkg/utils/isSSR';
import AnimatedGiftIcon from '@wix/da-ds/pkg/Icons/GiftAnimated';
import { BiData, GiftClickBiEvent } from '@wix/da-bi/pkg/events';
import { useSelector } from 'react-redux';
import { getUserGiftState } from '../redux/selectors';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { initGiftState } from '../redux/actions';
import { useRestrictToLoggedIn } from '../../utils/hooks/useRestrictToLoggedIn';
import ModuleLoaderLazy from '../redux/ModuleLoaderLazy';
import { useGiftMenuItems } from '../giftMenuItems';

export interface Props {
  user: PapiUser;
  className?: string;
  allowBirthdayAnimation?: boolean;
}

const GiveButton: React.FC<Props> = ({ user, className, allowBirthdayAnimation = false }) => {
  const { t } = useTranslation();
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const giftState = useSelector(state => getUserGiftState(state, user.useridUuid));

  const dispatchInitGiftState = useAction(initGiftState);
  const showAnimatedBirthdayIcon = allowBirthdayAnimation && user.isBirthweek;

  const giftMenuItems = useGiftMenuItems(user);

  const handleOpen = restrictToLoggedIn(ReferralType.GIVE, () => {
    if (!giftState.initStatus) {
      dispatchInitGiftState(user);
    }
  });

  // Since we load the redux for gifting dynamically, we show a noop icon button in the real button's place until
  // it's finished loading.
  const commonProps: Pick<
    IconButtonWithContextMenuProps,
    'size' | 'iconSizeOverride' | 'icon' | 'tooltipText'
    // Due to some typescript shenanigans in the IconButtonWithContextMenuProps type requiring an accessible label in at
    // least one of a few possible fields, including 'aria-label' in the Pick<> is more complicated that it would seem.
    //
    // To get around this, we add an intersection with a required 'aria-label'.
  > & { 'aria-label': string } = {
    size: 'medium',
    'aria-label': t('common.give'),
    iconSizeOverride: showAnimatedBirthdayIcon ? IconSize.LARGE : undefined,
    icon: showAnimatedBirthdayIcon ? AnimatedGiftIcon : GiftIcon16,
    tooltipText: showAnimatedBirthdayIcon
      ? t('common.giveBirthday', {
          username: user.username,
        })
      : t('common.give'),
  };

  return (
    <Suspense fallback={<IconButtonWithContextMenu {...commonProps} items={[]} loading disabled />}>
      <ModuleLoaderLazy />
      <IconButtonWithContextMenu
        {...commonProps}
        className={className}
        items={giftMenuItems}
        popperManagerProps={{
          onOpen: handleOpen,
        }}
        biData={BiData<GiftClickBiEvent>({
          evid: 274,
          recipientid: user.useridUuid,
        })}
        // Without these server checks, the client starts with the button enabled before hydration can happen. This results
        // in the icon flashing from active, to inactive, to active.
        loading={isServer()}
        disabled={isServer()}
      />
    </Suspense>
  );
};
GiveButton.displayName = 'GiveButton';

export default GiveButton;
